import * as React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * ButtonColorful — port de kokonutd/button-colorful (21st.dev) a la paleta
 * Winclap. Pill negro con una capa de gradiente (violeta → verde → celeste)
 * desenfocada detrás del label que se enciende en hover; la flecha se
 * desplaza en diagonal. `variant="secondary"`: fondo claro translúcido y el
 * gradiente aparece solo como borde en hover.
 *
 * Con `href` renderiza un link (SPA si empieza con "/", nueva pestaña si es
 * externo) con el mismo look; sin `href` es un <button>. No usa `asChild`:
 * el Slot de Radix exige un único hijo y acá hay varias capas.
 * `label` es azúcar para el caso más común; `children` se renderiza tal cual.
 */
export interface ButtonColorfulProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  href?: string;
  label?: string;
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  /** Ocultar la flecha (p. ej. botones de edición con su propio ícono). */
  hideArrow?: boolean;
}

const SIZE: Record<NonNullable<ButtonColorfulProps['size']>, string> = {
  sm: 'h-8 px-4 text-xs',
  md: 'h-11 px-6 text-sm',
  lg: 'h-12 px-7 text-base',
};

export const ButtonColorful = React.forwardRef<HTMLButtonElement, ButtonColorfulProps>(
  ({ className, label, variant = 'primary', size = 'md', hideArrow, href, children, type, ...props }, ref) => {
    const secondary = variant === 'secondary';
    const classes = cn(
      buttonVariants({ variant: 'default', size: 'default' }),
      'group relative overflow-hidden rounded-full font-semibold shadow-none active:scale-100',
      SIZE[size],
      secondary
        ? 'bg-white/80 text-brand-dark backdrop-blur-sm border border-border hover:bg-white/90 hover:shadow-none dark:bg-white/10 dark:text-white dark:border-white/15'
        : 'bg-brand-dark text-white hover:bg-brand-dark hover:shadow-none',
      className
    );
    const inner = (
      <>
        {secondary ? (
          // Borde con gradiente en hover: capa con el gradiente + máscara interior del color de fondo.
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          >
            <span className="absolute inset-0 rounded-full bg-linear-to-r from-brand-purple via-brand-green to-brand-blue" />
            <span className="absolute inset-px rounded-full bg-white dark:bg-brand-dark" />
          </span>
        ) : (
          <>
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-linear-to-r from-brand-purple via-brand-green to-brand-blue opacity-60 blur-xl transition-all duration-500 group-hover:opacity-100 group-hover:scale-110"
            />
            {/* El texto sigue legible con el gradiente a full */}
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-black/20 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
            />
          </>
        )}
        <span className="relative z-10 flex items-center justify-center gap-2">
          {children ?? <span>{label}</span>}
          {!hideArrow && (
            <ArrowUpRight
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
                secondary ? 'text-brand-dark/80 dark:text-white/80' : 'text-white/90'
              )}
            />
          )}
        </span>
      </>
    );
    if (href) {
      const anchorProps = props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      if (href.startsWith('/')) {
        return (
          <Link to={href} className={classes} {...anchorProps}>
            {inner}
          </Link>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={classes} {...anchorProps}>
          {inner}
        </a>
      );
    }
    return (
      <button ref={ref} type={type ?? 'button'} className={classes} {...props}>
        {inner}
      </button>
    );
  }
);
ButtonColorful.displayName = 'ButtonColorful';
